import { saveAs } from 'file-saver';
import { WsSelectObj } from 'wisestep-base';
import * as XLSX from 'xlsx';

import { BulkUploadResponseDTO } from '../types/BulkUploadResponseDTO';

export const generateAndDownloadExcel = <T>(
  bulkUploadResponse: BulkUploadResponseDTO<T>,
  fieldsToExclued: string[],
  fieldNamesMapping: WsSelectObj[],
  fileName = 'BulkUploadResults.xlsx'
) => {
  const workbook = XLSX.utils.book_new();

  Object.entries(bulkUploadResponse).forEach(([sheetName, records]) => {
    let worksheet;

    if (records && records.length > 0) {
      const flattenedData = records.map((record) =>
        flattenObject(record, '', {}, fieldsToExclued, fieldNamesMapping)
      );
      worksheet = XLSX.utils.json_to_sheet(flattenedData);

      // Below for loop is used to style the header row
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cellRef]) continue;

        worksheet[cellRef].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'E0E0E0' } },
        };
      }

      // Below code block is used to set the column width
      const colWidths = Object.keys(flattenedData[0]).map((key) => {
        const maxLength = Math.max(
          key.length,
          ...flattenedData.map((row) => String(row[key] || '').length)
        );
        return { wch: Math.min(maxLength + 2, 50) };
      });
      worksheet['!cols'] = colWidths;
    } else {
      worksheet = XLSX.utils.aoa_to_sheet([['No Data']]);
    }

    const formattedSheetName = convertCamelToRegularCase(sheetName);
    XLSX.utils.book_append_sheet(workbook, worksheet, formattedSheetName);
  });

  const binaryString = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'binary',
    bookSST: true,
    cellDates: true,
  });

  const buffer = new ArrayBuffer(binaryString.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binaryString.length; i++) {
    view[i] = binaryString.charCodeAt(i) & 0xff;
  }

  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, fileName);
};

const convertCamelToRegularCase = (camelCaseString: string): string => {
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

const flattenObject = (
  obj: any,
  parent = '',
  res: any = {},
  fieldsToExclued: string[] = [],
  fieldNamesMapping: WsSelectObj[] = []
) => {
  for (const key in obj) {
    let propName = parent ? `${parent}.${key}` : key;
    if (fieldsToExclued.includes(key)) {
      continue;
    }
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      flattenObject(obj[key], propName, res, fieldsToExclued);
    } else {
      propName = getExcelColumnName(fieldNamesMapping, propName);
      res[propName] = obj[key];
    }
  }
  return res;
};

const getExcelColumnName = (fieldNamesMapping: WsSelectObj[], key: string) => {
  return fieldNamesMapping.find((field) => field.value === key)?.label || key;
};
